import React, { useRef, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { UserFeedsType } from "../../types";
import { CancelEdit, ImageUpdate, ZoomImage } from "..";
import EmojiPicker, { EmojiClickData, Theme } from "emoji-picker-react";
import { CiImageOn, CiFaceSmile, CiUser } from "react-icons/ci";
import { ipfsGatewayImage } from "../../utils/ipfsGatewayImage";
import { showToast } from "../../utils/showToast";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { handleIPFSUploads } from "../../utils/iPfsUpload";
import { Rounded } from "../../assets/images";
import Resizer from "react-image-file-resizer";

interface FeedUpdateEditProps {
  setEditContent: React.Dispatch<React.SetStateAction<boolean>>;
  userFeed: UserFeedsType | undefined;
  setOnChange: React.Dispatch<React.SetStateAction<boolean>>;
  feedImage?: string[];
  handleUpdateFeed: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  loading: boolean;
  imageUploadLoading: boolean;
  updateFeedContent?: string;
  setUpdateFeedContent: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  backgroundColor?: string;
  onChange: boolean;
  feedVideos?: string[];
  toggle: boolean | (() => void);
  setImageCID: React.Dispatch<React.SetStateAction<string[]>>;
  imageCID: string[];
  setCompressedVideoCid: React.Dispatch<React.SetStateAction<string[]>>;
  compressedVideoCid: string[];
  ipfsVideos: string[];
  setIpfsVideos: React.Dispatch<React.SetStateAction<string[]>>;
  setThumbFile: React.Dispatch<React.SetStateAction<File[]>>;
  setCompressedVideosArray: React.Dispatch<React.SetStateAction<File[]>>;
}
const FeedUpdateEdit: React.FC<FeedUpdateEditProps> = ({
  setEditContent,
  setOnChange,
  userFeed,
  feedImage,
  loading,
  handleUpdateFeed,
  imageUploadLoading,
  setUpdateFeedContent,
  updateFeedContent,
  backgroundColor,
  feedVideos,
  toggle,
  setImageCID,
  imageCID,
  setCompressedVideoCid,
  compressedVideoCid,
  onChange,
  setThumbFile,
  setCompressedVideosArray,
}) => {
  const imageURL = feedImage || [];
  const videoURL = feedVideos || [];
  const [imageUpload, setImageUpload] = React.useState<string[]>(
    imageURL ?? []
  );
  const [openEmojiPicker, setOpenEmojiPicker] = React.useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [openUpload, setOpenUpload] = React.useState(false);
  const [cancelEdit, setCancelEdit] = useState(false);
  const [loadImages, setLoadImages] = useState(false);
  const [selectedVideos, setSelectedVideos] = useState<any[]>(videoURL || []);
  const [isVideoCompressing, setIsVideoCompressing] = useState<boolean>(false);
  const [allMediaUrl, setAllMediaUrl] = useState<string[]>([
    ...(videoURL ?? []),
    ...(imageURL ?? []),
  ]);
  // const [ipfsVideos, setIpfsVideos] = useState<string[]>([])
  // console.log("videoURL: ", videoURL);
  // console.log("allMediaUrl: ", allMediaUrl);

  const [thumbnail, setThumbnail] = useState<string[]>([]);
  const totalImages = feedImage?.length || 0;
  // console.log(allMediaUrl, "allMediaUrl just created");

  const onClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.isCustom ? emojiData.unified : emojiData.emoji;
    setUpdateFeedContent((inputValue) => inputValue + emoji + inputValue);
  };

  const onVideoSelectFile = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles) return;

    try {
      const selectedFilesArray = Array.from(selectedFiles);
      setOnChange(true);
      const allThumbnails: string[] = [];
      const allFileThumb: File[] = [];
      const compressedVideosArray: File[] = [];

      for (const selectedFile of selectedFilesArray) {
        const videoObjectUrl = URL.createObjectURL(selectedFile);
        const videoPlayer = document.createElement("video");
        videoPlayer.src = videoObjectUrl;

        await new Promise((resolve) => {
          videoPlayer.onloadedmetadata = () => resolve(undefined);
        });

        const duration = videoPlayer.duration;
        const maxDuration = 120;

        if (duration > maxDuration) {
          showToast(
            `Video duration exceeds the maximum allowed (${maxDuration} seconds).`,
            "warn"
          );
          setOnChange(false);
          return;
        }
        setIsVideoCompressing(true);

        const canvas = document.createElement("canvas");
        canvas.width = videoPlayer.videoWidth;
        canvas.height = videoPlayer.videoHeight;
        const ctx = canvas.getContext("2d");

        videoPlayer.currentTime = 1;

        await new Promise((resolve) => {
          videoPlayer.onseeked = () => {
            ctx?.drawImage(videoPlayer, 0, 0, canvas.width, canvas.height);
            resolve(undefined);
          };
        });

        const thumbnailDataUrl = canvas.toDataURL("image/jpeg");
        const thumbnailBlob = await new Promise<Blob>((resolve) =>
          canvas.toBlob((blob) => resolve(blob as Blob), "image/jpeg")
        );

        const thumbnailFile = new File(
          [thumbnailBlob],
          `${selectedFile.name}`,
          {
            type: "image/jpeg",
            lastModified: Date.now(),
          }
        );

        allFileThumb.push(thumbnailFile);
        allThumbnails.push(thumbnailDataUrl);

        const arrayBuffer = await selectedFile.arrayBuffer();
        const worker = new FFmpeg();
        await worker.load();

        const uint8Array = new Uint8Array(arrayBuffer);
        await worker.writeFile("input.mp4", uint8Array);
        await worker.exec(["-i", "input.mp4", "-c", "copy", "output.mp4"]);

        const data = await worker.readFile("output.mp4");
        const compressedVideoBlob = new Blob([data], { type: "video/mp4" });

        const compressedVideoFile = new File(
          [compressedVideoBlob],
          `compressed_${selectedFile.name}`,
          {
            type: "video/mp4",
            lastModified: Date.now(),
          }
        );

        compressedVideosArray.push(compressedVideoFile);
        await worker.terminate();
      }

      // console.log("compressedVideosArray: ", compressedVideosArray);
      setCompressedVideosArray((prevCompressedVideos) => [
        ...prevCompressedVideos,
        ...compressedVideosArray,
      ]);

      setThumbFile((thumb) => [...thumb, ...allFileThumb]);
      setThumbnail((prevThumbnail) => [...prevThumbnail, ...allThumbnails]);

      const fileToStringVideo = selectedFilesArray.map((video) =>
        URL.createObjectURL(video)
      );
      const updatedPreview = [...selectedVideos, ...fileToStringVideo];
      setSelectedVideos(updatedPreview);

      setAllMediaUrl([...allMediaUrl, ...fileToStringVideo]);
      // setCompressedVideoCid
      setIsVideoCompressing(false);
    } catch (error) {
      console.error("Error processing videos:", error);
      setIsVideoCompressing(false);
    }

    // Clear input value to allow selecting the same file again
    event.target.value = "";
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const selectedImages = e.target.files;
    if (selectedImages) {
      setLoadImages(true);
      setOnChange(true);
      const newImageArray = Array.from(selectedImages);

      // Resize and add images to the state
      const resizedImages: File[] = [];
      const resizedPreviews: string[] = [];

      for (const image of newImageArray) {
        await new Promise<void>((resolve) => {
          Resizer.imageFileResizer(
            image,
            700, // Width
            679, // Height
            "JPEG", // Format
            100, // Quality
            0, // Rotation
            (uri) => {
              // Resized image
              if (typeof uri === "string") {
                resizedPreviews.push(uri);
                const file = dataURLtoFile(uri, image.name);
                console.log(file);

                resizedImages.push(file);
              }
              resolve();
            }
            // "base64", // Output type
            // 200, // Max width
            // 200 // Max height
          );
        });
      }

      const updatedImages = resizedImages.map((imageArray) => imageArray);
      const image: any = await handleIPFSUploads(updatedImages);

      const filteredImages =
        image && image.filter((img: any) => img !== undefined);

      //  [...images, ...newImageArray];
      Promise.all(filteredImages)
        .then((updates) => {
          if (imageURL) {
            setImageCID([...imageURL, ...updates]);
          } else {
            setImageCID((prevImage) => [...prevImage, ...updates]);
          }
        })
        .then(() => {
          setLoadImages(false);
        });
      // console.log(imageCID, "imageCID");

      // const newPreviewArray = newImageArray.map((image) =>
      //   URL.createObjectURL(image)
      // );
      const updatedPreviews = [...imageUpload, ...resizedPreviews];
      setImageUpload(updatedPreviews);

      const updatedMediaUrls = [...allMediaUrl, ...resizedPreviews];
      setAllMediaUrl(updatedMediaUrls);
    }
  };

  const dataURLtoFile = (dataUrl: string, fileName: string) => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  };

  const handleCloseEdit = () => {
    setEditContent(false);
    setImageUpload([]);
    setImageCID(imageURL);
    setSelectedVideos([]);
    setCompressedVideoCid(videoURL);
    setAllMediaUrl([...videoURL, ...imageURL]);
    setUpdateFeedContent(userFeed?.content);
    if (typeof toggle === "function") {
      toggle();
    }
  };

  const handleCloseCreate = () => {
    setImageUpload([]);
    setOpenUpload(false);
    setImageCID(imageURL);
    setSelectedVideos([]);
    setAllMediaUrl([...videoURL, ...imageURL]);
    setCompressedVideoCid(videoURL);
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = e.target.value.trim(); // Trim whitespace from the input value
    setUpdateFeedContent(e.target.value); // Update the post state with the input value

    if (userFeed && userFeed.content) {
      if (
        inputValue.trim().length !== userFeed?.content?.trim().length ||
        imageUpload.length > 0
      ) {
        setOnChange(true); // Enable the button if there is non-whitespace content
      } else {
        setOnChange(false);
      }
    } else if (!userFeed?.content) {
      if (inputValue.trim().length > 0) {
        setOnChange(true);
      } else {
        setOnChange(false);
      } // Disable the button if the content is empty or contains only whitespace
    }
  };

  const totalMedias = allMediaUrl.length || 0;

  const pImage =
    userFeed?.creator && userFeed.creator.ecobookProfile?.profileImage;

  const [showAllImages, setShowAllImages] = React.useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const handleClick = (index: number) => {
    setShowAllImages(index);
    setCurrentIndex(index);
  };
  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex < totalMedias - 1 ? prevIndex + 1 : prevIndex
    );
  };
  const totalVideos = selectedVideos.length || 0;

  const deleteVideo = (indexToDelete: number) => {
    setOnChange(true);
    // Filter out the video to delete from selectedVideos array
    const updatedSelectedVideos = selectedVideos.filter(
      (video, index) => index !== indexToDelete
    );
    setSelectedVideos(updatedSelectedVideos);

    // Filter out the corresponding compressed video CID from compressedVideoCid array
    const updatedCompressedVideoCid = compressedVideoCid.filter(
      (cid, index) => index !== indexToDelete
    );
    setCompressedVideoCid(updatedCompressedVideoCid);

    const updatedMediaUrls = allMediaUrl.filter(
      (_, index) => index !== indexToDelete
    );
    setAllMediaUrl(updatedMediaUrls);

    if (updatedSelectedVideos.length > 1) {
      handleNext();
    } else {
      handlePrev();
    }
  };

  const removeMedia = (indexToRemove: number) => {
    setOnChange(true);

    setImageCID(imageCID.filter((_, index) => index !== indexToRemove));

    console.log(imageCID, "imageCID");

    setImageUpload((prevImageUpload) =>
      prevImageUpload.filter((_, index) => index !== indexToRemove)
    );

    const updatedMediaUrls = allMediaUrl.filter(
      (_, index) => index !== indexToRemove
    );
    setAllMediaUrl(updatedMediaUrls);

    if (updatedMediaUrls.length > 1) {
      handleNext();
    } else {
      handlePrev();
    }
  };

 
  console.log(onChange, "onChange");
  console.log(totalMedias, "totalMedias");
  console.log(loading, "loading");
  console.log(imageUploadLoading, "imageUploadLoading");

  return (
    <div className="fixed picModal inset-0 z-30 flex items-center justify-center backdrop-filter backdrop-blur-[10px] bg-[#FFFFFFC2] overflow-y-hidden">
      <div className=" flex flex-col rounded-lg lg:w-[32%] shadow-lg bg-white">
        <div className="flex justify-between h-[10%] items-center border-b border-b-[#D8D3D3] pr-7 py-1">
          <div className="flex items-center flex-1 text-xl font-Poppins font-medium justify-center text-[#000]">
            Edit
          </div>
          <button
            className="py-2 px-2
            rounded-full text-[#33363F]
            bg-[#ECE6E6] active:bg-bgGrey
            hover:text-gray-600"
          >
            <AiOutlineClose size={20} onClick={() => setCancelEdit(true)} />
          </button>
        </div>
        {cancelEdit && (
          <CancelEdit
            handleCloseEdit={handleCloseEdit}
            setCancelEdit={setCancelEdit}
          />
        )}
        <div className="px-[29px] flex flex-col pt-3 pb-8">
          <div className="">
            <div className="flex w-[70%] gap-2 mb-2 font-Poppins">
              {/* <img src={Astro} alt="" className="w-11 h-11 rounded-full" /> */}
              {pImage ? (
                <img
                  alt=""
                  src={ipfsGatewayImage(pImage!)}
                  className="lg:h-11 lg:w-11 w-[30px] h-[30px] rounded-full"
                  loading="lazy"
                />
              ) : (
                <div className="lg:h-11 lg:w-11 w-[30px] h-[30px] bg-slate-100 flex items-center justify-center rounded-full">
                  <CiUser size={20} />
                </div>
              )}
              <div className="flex h-9 flex-col w-[70%]">
                <div className="text-[#222] font-medium text-[13.5px]">
                  {`${userFeed?.creator.firstName} ${userFeed?.creator.lastName}`}
                </div>
                <div className="rounded-sm bg-[rgba(33,87,89,0.005)] w-[35%] h-[19.24px] flex justify-center items-center lg:text-[11.2px] text-[8px] text-midGreen">
                  {/* {!community ? `${communityFeed?.community?.name} COMMUNITY` : communityFeed?.creator?.roles! } */}
                  Public
                </div>
              </div>
            </div>
          </div>
          {allMediaUrl && showAllImages !== null && totalMedias > 0 && (
            <ZoomImage
              setShowAllImages={setShowAllImages}
              handleNext={handleNext}
              handlePrev={handlePrev}
              totalImages={totalMedias}
              image={allMediaUrl}
              currentIndex={currentIndex}
              selectedVideos={selectedVideos}
              imageUpload={imageUpload}
              removeMedia={removeMedia}
              deleteVideo={deleteVideo}
            />
          )}
          <form className="w-full relative" onSubmit={handleUpdateFeed}>
            <textarea
              value={updateFeedContent}
              onChange={handleOnChange}
              className={`resize-none pr-5 h-[50%] w-full outline-none rounded p-3 block box-border font-SegoeUI placeholder:text-[19.3px] placeholder:font-normal placeholder:text-[#777272] placeholder:font-SegoeUI  overscroll-auto disabled:overflow-y-hidden disabled:bg-blend-overlay ${
                updateFeedContent && updateFeedContent?.length > 50
                  ? "text-[19.3px]"
                  : "text-2xl"
              }`}
              rows={4}
              cols={30}
              style={{ background: backgroundColor }}
            />
            {isVideoCompressing && (
              <div className="fixed inset-0 z-30 flex items-center justify-center backdrop-filter backdrop-blur-[10px] bg-[#FFFFFFC2]">
                <img src={Rounded} alt="" className="h-10 w-10 animate-spin" />
              </div>
            )}
            {(openUpload ||
              (feedImage && feedImage?.length > 0) ||
              (feedVideos && feedVideos.length > 0)) && (
              <ImageUpdate
                imageUpload={imageUpload}
                totalImages={totalImages}
                handleImageChange={handleImageChange}
                handleCloseCreate={handleCloseCreate}
                setShowAllImages={setShowAllImages}
                handleClick={handleClick}
                imageLoading={loadImages}
                onVideoSelectFile={onVideoSelectFile}
                selectedVideos={selectedVideos}
                totalVideos={totalVideos}
                allMediaUrls={allMediaUrl}
                thumbnail={thumbnail}
              />
            )}

            <div className="w-full h-[41px] pr-11 bg-[#F6F6F6] rounded-[10px] mt-4 flex items-center justify-between pl-[18px] mb-4">
              <h1 className="text-[#777272] font-SegoeUI text-xs font-normal">
                Add to your post
              </h1>
              <div className="flex gap-3">
                <CiImageOn
                  size={24}
                  onClick={() => setOpenUpload(true)}
                  className="cursor-pointer"
                />
                <CiFaceSmile
                  size={24}
                  className="cursor-pointer active:fill-red-200"
                  onClick={() => setOpenEmojiPicker(!openEmojiPicker)}
                />
              </div>
              {openEmojiPicker && (
                <div
                  className="absolute bottom-28 right-0 ease-in-out duration-75 w-[60%] rounded text-[9px] font-Poppins"
                  ref={containerRef}
                >
                  <EmojiPicker
                    onEmojiClick={onClick}
                    height={300}
                    theme={Theme.AUTO}
                    previewConfig={{
                      showPreview: false,
                    }}
                  />
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={
                !onChange || loading || imageUploadLoading
              }
              className={`w-full h-[41px] mt-4 flex items-center justify-center rounded-[10px] text-base font-Poppins font-medium text-white bg-midGreen cursor-pointer ${
                (loading || imageUploadLoading) &&
                "cursor-not-allowed bg-[#D7D7D7]"
              }`}
            >
              {loading || imageUploadLoading ? "Updating..." : "Update"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeedUpdateEdit;
